import { createSlice } from '@reduxjs/toolkit';

// IMPORTANT: Don't load from localStorage during slice initialization!
// This allows us to have full control over when/how events are loaded
// Events should only come from config.onboardingEvents or WebSocket messages

const initialState = {
  videoInfo: null,
  isLiveModalOpen: false,
  clearEventsFlag: false, // Flag to trigger clearing events in LiveModal
  eventHistory: [], // Always start empty - load from localStorage in App.js useEffect only
  currentEventIndex: -1, // Always start at -1 - load from localStorage in App.js useEffect only
  isOnboardingMode: false, // Flag to indicate onboarding mode
};

const liveSlice = createSlice({
  name: 'live',
  initialState,
  reducers: {
    setLiveVideo: (state, action) => {
      state.videoInfo = action.payload;
    },
    clearLiveVideo: (state) => {
      state.videoInfo = null;
    },
    openLiveModal: (state) => {
      state.isLiveModalOpen = true;
    },
    closeLiveModal: (state) => {
      state.isLiveModalOpen = false;
    },
    setLiveModalOpen: (state, action) => {
      state.isLiveModalOpen = action.payload;
    },
    addEventToHistory: (state, action) => {
      const eventType = action.payload?.type;
      if (eventType === 'call' || eventType === 'tour') return;
      state.eventHistory.push(action.payload);
      // Save to localStorage
      try {
        localStorage.setItem('liveEventHistory', JSON.stringify(state.eventHistory));
        console.log('💾 Saved event history to localStorage:', state.eventHistory.length, 'events');
      } catch (error) {
        console.error('Error saving event history to localStorage:', error);
      }
    },
    setCurrentEventIndex: (state, action) => {
      state.currentEventIndex = action.payload;
      // Save to localStorage
      try {
        localStorage.setItem('liveEventIndex', action.payload.toString());
        console.log('💾 Saved current event index to localStorage:', action.payload);
      } catch (error) {
        console.error('Error saving event index to localStorage:', error);
      }
    },
    clearLiveEvents: (state) => {
      state.clearEventsFlag = !state.clearEventsFlag; // Toggle flag to trigger clearing
      
      // Fully clear all events (including onboarding) to prevent duplication when config changes
      const previousLength = state.eventHistory.length;
      state.eventHistory = [];
      state.currentEventIndex = -1;
      state.videoInfo = null; // CRITICAL: Clear videoInfo to prevent old event from being re-added
      state.isLiveModalOpen = false; // Close modal when clearing
      
      console.log('🗑️ clearLiveEvents reducer called - Removed:', previousLength, 'events, eventHistory is now:', state.eventHistory.length, 'events');
      console.log('🚫 Cleared videoInfo and closed modal to prevent re-adding old events');
      
      // Clear from localStorage
      try {
        localStorage.setItem('liveEventHistory', JSON.stringify(state.eventHistory));
        localStorage.setItem('liveEventIndex', state.currentEventIndex.toString());
        console.log('✅ clearLiveEvents - Both Redux and localStorage fully cleared');
      } catch (error) {
        console.error('Error saving event history to localStorage:', error);
      }
    },
    setOnboardingMode: (state, action) => {
      state.isOnboardingMode = action.payload;
    },
  },
});

export const { 
  setLiveVideo, 
  clearLiveVideo, 
  openLiveModal, 
  closeLiveModal, 
  setLiveModalOpen, 
  addEventToHistory,
  setCurrentEventIndex,
  clearLiveEvents,
  setOnboardingMode
} = liveSlice.actions;

export default liveSlice.reducer;
